import { domToPng } from 'modern-screenshot';
import { useCallback, useRef, useState } from 'react';

import { rewindConfig } from 'components/rewind';

import { RewindSlideId } from '../rewindModal.types';

type RewindCapturedSlide = {
  slideId: RewindSlideId;
  blob: Blob;
  dataUrl: string;
  timestamp: number;
};

type UseRewindCaptureReturn = {
  /** Capture a specific slide element and return as Blob and data URL */
  captureSlide: (
    element: HTMLElement,
    slideId: RewindSlideId,
  ) => Promise<RewindCapturedSlide>;
  /** Capture a slide and immediately save it as a file */
  captureAndSave: (
    element: HTMLElement,
    slideId: RewindSlideId,
    filename?: string,
  ) => Promise<void>;
  /** Capture a slide and copy to clipboard */
  captureAndCopyToClipboard: (
    element: HTMLElement,
    slideId: RewindSlideId,
  ) => Promise<boolean>;
  /** Get all captured slides from the cache */
  getCapturedSlides: () => Map<RewindSlideId, RewindCapturedSlide>;
  /** Clear the captured slides cache */
  clearCache: () => void;
  /** Whether a capture operation is in progress */
  isCapturing: boolean;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Whether a copy to clipboard operation is in progress */
  isCopying: boolean;
  /** Last error that occurred during capture */
  captureError: Error | null;
};

/**
 * Hook for capturing Rewind slides as images. Supports downloading, copying to
 * clipboard, and caching captured slides.
 */
export function useRewindCapture(): UseRewindCaptureReturn {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [captureError, setCaptureError] = useState<Error | null>(null);
  const capturedSlidesRef = useRef<Map<RewindSlideId, RewindCapturedSlide>>(
    new Map(),
  );

  /** Capture a specific slide element using html-to-image */
  const captureSlide = useCallback(
    async (
      element: HTMLElement,
      slideId: RewindSlideId,
    ): Promise<RewindCapturedSlide> => {
      setIsCapturing(true);
      setCaptureError(null);

      try {
        // Use modern-screenshot to capture the element as PNG data URL
        const dataUrl = await domToPng(element, {
          scale: 2,
          // Timeout for loading media and fetching remote assets
          timeout: 15000,

          // Make watermark visible in the cloned DOM
          onCloneEachNode: (node) => {
            if (node instanceof HTMLElement) {
              // Find and show the watermark in the clone
              if (node.hasAttribute('data-capture-watermark')) {
                node.style.display = 'flex';
              }
            }
          },
          // Skip elements that might cause issues
          filter: (node) => {
            if (node instanceof HTMLElement) {
              return !node.hasAttribute('data-capture-ignore');
            }
            return true;
          },
        });

        // Convert data URL to blob manually (fetch(dataUrl) hangs in Firefox)
        const base64Data = dataUrl.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'image/png' });

        const capturedSlide: RewindCapturedSlide = {
          slideId,
          blob,
          dataUrl,
          timestamp: Date.now(),
        };

        // Cache the captured slide
        capturedSlidesRef.current.set(slideId, capturedSlide);

        return capturedSlide;
      } catch (error) {
        const captureErr =
          error instanceof Error ? error : new Error('Capture failed');
        setCaptureError(captureErr);
        throw captureErr;
      } finally {
        setIsCapturing(false);
      }
    },
    [],
  );

  /** Capture a slide and save it as an image file */
  const captureAndSave = useCallback(
    async (
      element: HTMLElement,
      slideId: RewindSlideId,
      filename?: string,
    ): Promise<void> => {
      setIsSaving(true);

      try {
        const captured = await captureSlide(element, slideId);

        const downloadFilename =
          filename ?? `dixa-rewind-${rewindConfig.year}-${slideId}.png`;
        const url = URL.createObjectURL(captured.blob);

        try {
          const link = document.createElement('a');
          link.href = url;
          link.download = downloadFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } finally {
          URL.revokeObjectURL(url);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [captureSlide],
  );

  /**
   * Capture a slide and copy it to the clipboard Returns true if successful,
   * false otherwise Note: Clipboard API requires HTTPS and user gesture
   */
  const captureAndCopyToClipboard = useCallback(
    async (element: HTMLElement, slideId: RewindSlideId): Promise<boolean> => {
      setIsCopying(true);
      setCaptureError(null);

      try {
        // Check if Clipboard API is available
        if (!navigator.clipboard?.write) {
          console.warn(
            'Clipboard API not available. Falling back to data URL copy.',
          );
          const captured = await captureSlide(element, slideId);
          await navigator.clipboard.writeText(captured.dataUrl);
          return true;
        }

        // Firefox requires clipboard.write() to be called synchronously during
        // user activation. By passing a Promise to ClipboardItem, we initiate
        // the write immediately while the capture happens asynchronously.
        const blobPromise = (async () => {
          const dataUrl = await domToPng(element, {
            scale: 2,
            // Timeout for loading media and fetching remote assets
            timeout: 15000,
            onCloneEachNode: (node) => {
              if (node instanceof HTMLElement) {
                if (node.hasAttribute('data-capture-watermark')) {
                  node.style.display = 'flex';
                }
              }
            },
            filter: (node) => {
              if (node instanceof HTMLElement) {
                return !node.hasAttribute('data-capture-ignore');
              }
              return true;
            },
          });

          // Convert data URL to blob manually (fetch(dataUrl) hangs in Firefox)
          const base64Data = dataUrl.split(',')[1];
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return new Blob([bytes], { type: 'image/png' });
        })();

        const clipboardItem = new ClipboardItem({
          'image/png': blobPromise,
        });

        await navigator.clipboard.write([clipboardItem]);
        return true;
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        setCaptureError(
          error instanceof Error ? error : new Error('Clipboard copy failed'),
        );
        return false;
      } finally {
        setIsCopying(false);
      }
    },
    [captureSlide],
  );

  /** Get all captured slides from cache */
  const getCapturedSlides = useCallback((): Map<
    RewindSlideId,
    RewindCapturedSlide
  > => {
    return new Map(capturedSlidesRef.current);
  }, []);

  /** Clear the captured slides cache */
  const clearCache = useCallback(() => {
    capturedSlidesRef.current.clear();
  }, []);

  return {
    captureSlide,
    captureAndSave,
    captureAndCopyToClipboard,
    getCapturedSlides,
    clearCache,
    isCapturing,
    isSaving,
    isCopying,
    captureError,
  };
}
